import { Alert, Box, Dialog, FormControlLabel, Link } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { FC, useCallback, useEffect, useState } from 'react';
import { usePatientProfile } from '../../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../../services/api-client/contact-api';
import { TreatmentPlanItemModel, TreatmentPlanModel } from '../../../../../../../services/api-client/contact-api.types';
import { OrangeGreenSwitch } from '../../../../../../shared/switch/switch.styles';
import { TreatmentPlanStatusComponent } from '../treatment-plan-status';
import {
  CloseDialogButton,
  DialogContentWrapper,
  DialogSubtitle,
  DialogTitle,
  DialogWrapper,
  StatusWrapper
} from './treatment-plan-items-dialog.styles';
import { TreatmentPlanItemsTable } from './treatment-plan-items-table/treatment-plan-items-table';

export interface TreatmentPlanItemsDialogProps {
  isOpen: boolean;
  treatmentPlan: TreatmentPlanModel;
  onClose: () => void;
  onTreatmentPlanUpdate?: (treatmentPlan: TreatmentPlanModel) => void;
}

export const TreatmentPlanItemsDialog: FC<TreatmentPlanItemsDialogProps> = (props) => {
  const { isOpen, treatmentPlan, onTreatmentPlanUpdate, onClose } = props;
  const { config } = useConfig();
  const { getClaims } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [isSameLocationSession, setIsSameLocationSession] = useState(false);
  const contactApi = ContactApi.getInstance();

  useEffect(() => {
    const checkLocation = async () => {
      const claims = await getClaims();
      setIsSameLocationSession(claims.locationId == locationId);
    };

    checkLocation();
  }, []);

  const handleAutoFollowUpChange = async (e: any) => {
    setIsLoading(true);

    try {
      onTreatmentPlanUpdate?.({ ...treatmentPlan, isAutoFollowUp: e.target.checked });
      await contactApi.updateTreatmentPlan(contactDetails.id, locationId, treatmentPlan.id, {
        isAutoFollowUp: e.target.checked
      });
    } catch (error) {
      onTreatmentPlanUpdate?.({ ...treatmentPlan, isAutoFollowUp: !e.target.checked });
      showError('Unable to update Auto Follow Up settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const treatmentPlanItemChange = (item: TreatmentPlanItemModel) => {
    onTreatmentPlanUpdate?.({
      ...treatmentPlan,
      treatmentPlanItems: treatmentPlan.treatmentPlanItems.map((i) => (i.id === item.id ? item : i))
    });
  };

  const getAutoFollowUpLabel = useCallback(() => {
    if (!treatmentPlan.isAutomaticCommunicationActive) return 'Follow Up Disabled';
    else if (treatmentPlan.isAutoFollowUp) return 'Auto Follow Up On';
    else if (!treatmentPlan.isAutoFollowUp) return 'Auto Follow Up Off';
    else return '';
  }, [treatmentPlan]);

  return (
    <>
      <Dialog open={isOpen} maxWidth={false}>
        <DialogWrapper>
          <DialogTitle>
            {`${contactDetails.firstName} ${contactDetails.lastName}`}
            <CloseDialogButton onClick={onClose} />
          </DialogTitle>
          <DialogContentWrapper>
            <DialogSubtitle>
              <Box>{treatmentPlan?.description}</Box>
              <Box sx={{ margin: '0 28px 0 auto', display: 'flex', fontWeight: 400, fontSize: '14px' }}>
                {treatmentPlan.dateCreated ? moment(treatmentPlan.dateCreated).format('MM/DD/YYYY') : null}
              </Box>
            </DialogSubtitle>
            <StatusWrapper>
              <Box sx={{ display: 'flex' }}>
                Status: &nbsp;
                <TreatmentPlanStatusComponent status={treatmentPlan?.status} />
              </Box>
              <FormControlLabel
                sx={{ margin: 0 }}
                control={
                  <OrangeGreenSwitch
                    color="default"
                    checked={treatmentPlan.isAutoFollowUp}
                    onClick={handleAutoFollowUpChange}
                    disabled={isLoading || !treatmentPlan.isAutomaticCommunicationActive}
                  />
                }
                label={<Box sx={{ fontSize: '13px' }}>{getAutoFollowUpLabel()}</Box>}
                disabled={isLoading || !treatmentPlan.isAutomaticCommunicationActive}
              />
            </StatusWrapper>
            {!treatmentPlan.isAutomaticCommunicationActive && (
              <Alert severity="warning" sx={{ margin: '10px 30px 20px 30px', alignItems: 'center', cursor: 'default' }}>
                In order to enable followup for this treatment plan the Treatment Plan Campaign needs to be
                active.&nbsp;
                {isSameLocationSession && (
                  <>
                    Click{' '}
                    <Link
                      href={config.practice.treatmentPlanCampaignUrl}
                      sx={{ color: 'rgb(102, 60, 0)', textDecorationColor: 'rgb(102, 60, 0)' }}
                    >
                      here
                    </Link>{' '}
                    to update the campaign configuration.
                  </>
                )}
              </Alert>
            )}
            <TreatmentPlanItemsTable
              treatmentPlan={props.treatmentPlan}
              onItemChange={treatmentPlanItemChange}
              isTreatmentPlanCampaignActive={treatmentPlan.isAutomaticCommunicationActive}
              sx={{ width: '100%', margin: 0 }}
            />
            <br />
          </DialogContentWrapper>
        </DialogWrapper>
      </Dialog>
    </>
  );
};
